import { create } from 'zustand';
import { camelCase, map, uniq } from 'lodash';

interface Permission {
  write?: boolean;
}

type AuthPermissionsState = {
  permissions?: Record<string, Permission>;
  setPermissions: (_permissions: string[]) => void;
  clear: () => void;
};

export const useAuthPermissions = create<AuthPermissionsState>(set => ({
  setPermissions: _permissions => {
    // Define independent permissions that are always write-enabled
    const alwaysWritePermissions = ['fslForms.review.daypart', 'fslForms.review.eod'];

    // Get all unique identifiers
    const identifiers = uniq(
      map(_permissions, identifier => {
        if (alwaysWritePermissions.includes(identifier)) return identifier;
        if (!/\S*([^/\s]*(view|edit)\b\S*)/.test(identifier)) return identifier;
        return identifier.split('.').slice(0, -1).join('.');
      }),
    );

    // Check each identifier if contain atleast 1 "edit"
    const permissions: Record<string, { write: boolean }> = {};

    identifiers.forEach(_key => {
      const isWrite = _permissions.some(_permission => {
        if (!_permission.includes(_key)) return false;
        // If the key is in the alwaysWritePermissions list, force write to true
        if (alwaysWritePermissions.includes(_permission)) return true;
        return /\S*([^/\s]*edit\b\S*)/.test(_permission);
      });

      permissions[camelCase(_key)] = { write: isWrite };
    });

    set({ permissions });
  },
  clear: () => set({ permissions: undefined }),
}));
